import React from 'react';
import { PROGRAM_PAGE_URLS } from '@/shared/utils/programInfo';
import { trainingSessionsFor } from '@/shared/utils/trainingSessions';
import YesNoQuestion from './YesNoQuestion';
import { hasError } from './shared';
import {
    LA_ATTENDANCE_REFUSAL,
    LA_ATTENDANCE_REFUSAL_NOMINEE,
    LA_ERROR_MESSAGES,
    LA_IDS,
    LA_QUESTIONS,
} from './laAnswers';

// The refusal text with a pointer to the program's page, where its contact
// details are.
export function AttendanceRefusal({ nominee = false }) {
    return (
        <>
            {nominee ? LA_ATTENDANCE_REFUSAL_NOMINEE : LA_ATTENDANCE_REFUSAL}{' '}
            <a
                href={PROGRAM_PAGE_URLS.la}
                target="_blank"
                rel="noopener noreferrer"
            >
                LA program page
                <span className="visually-hidden"> (opens in a new tab)</span>
            </a>
        </>
    );
}

// The new-or-returning question and, for a new LA, the two attendance
// questions, asked in the Eligibility step so a No stops the student before
// they rank positions. A change to the new-or-returning answer calls onChange
// once per field it affects, so onChange must apply each call to the latest
// answers (a functional state update).
function LaEligibility({ answers, onChange, errorIds, context = {} }) {
    const { isReturning, canAttendOrientation, canAttendPedagogy } = answers;
    const isNew = isReturning === false;
    const trainings =
        isReturning === null
            ? []
            : trainingSessionsFor(context.trainingSessions, isReturning);

    const changeReturning = (value) => {
        onChange('isReturning', value);
        onChange('canAttendOrientation', null);
        onChange('canAttendPedagogy', null);
        onChange('commitmentsAcknowledged', false);
    };

    return (
        <div>
            <h2 className="h5 mb-3">Learning Assistant Eligibility</h2>

            <YesNoQuestion
                id="la-is-returning"
                legend={LA_QUESTIONS.isReturning}
                value={isReturning}
                onChange={changeReturning}
                invalid={hasError(errorIds, LA_IDS.isReturning)}
                message={LA_ERROR_MESSAGES[LA_IDS.isReturning]}
            />

            {isNew && (
                <>
                    <YesNoQuestion
                        id="la-can-attend-orientation"
                        legend={LA_QUESTIONS.canAttendOrientation}
                        value={canAttendOrientation}
                        onChange={(value) =>
                            onChange('canAttendOrientation', value)
                        }
                        invalid={
                            canAttendOrientation === false ||
                            hasError(errorIds, LA_IDS.canAttendOrientation)
                        }
                        message={
                            canAttendOrientation === false ? (
                                <AttendanceRefusal />
                            ) : (
                                LA_ERROR_MESSAGES[LA_IDS.canAttendOrientation]
                            )
                        }
                    />

                    <YesNoQuestion
                        id="la-can-attend-pedagogy"
                        legend={LA_QUESTIONS.canAttendPedagogy}
                        value={canAttendPedagogy}
                        onChange={(value) =>
                            onChange('canAttendPedagogy', value)
                        }
                        invalid={
                            canAttendPedagogy === false ||
                            hasError(errorIds, LA_IDS.canAttendPedagogy)
                        }
                        message={
                            canAttendPedagogy === false ? (
                                <AttendanceRefusal />
                            ) : (
                                LA_ERROR_MESSAGES[LA_IDS.canAttendPedagogy]
                            )
                        }
                    />
                </>
            )}
            {trainings.length > 0 && (
                <div className="mb-3">
                    <p className="small mb-1">
                        {`Dated trainings for ${isNew ? 'new' : 'returning'} Learning Assistants this term:`}
                    </p>
                    <ul className="small mb-0">
                        {trainings.map(({ title, when }) => (
                            <li key={`${title}-${when}`}>
                                <span className="fw-semibold">{title}:</span>{' '}
                                {when}
                            </li>
                        ))}
                    </ul>
                </div>
            )}
        </div>
    );
}

export default LaEligibility;
